import { useEffect, useRef } from "react";

const outcomes = [
  {
    icon: "ri-focus-3-line",
    title: "Capture Every Opportunity",
    desc: "Never miss high-value inbound leads. Intelligent intake systems qualify, score, and route prospects in real time — before they reach a human hand.",
    tag: "Lead Intelligence",
    num: "01",
  },
  {
    icon: "ri-flashlight-fill",
    title: "Accelerate Decision Cycles",
    desc: "Reduce friction in underwriting, qualification, and approvals. Automated logic moves deals forward while your team stays focused on judgment calls.",
    tag: "Process Automation",
    num: "02",
  },
  {
    icon: "ri-database-2-line",
    title: "Centralize Operational Intelligence",
    desc: "Unify your data, pipeline, and communications into one coherent system. Eliminate silos, reduce overhead, and operate from a single source of truth.",
    tag: "Data Unification",
    num: "03",
  },
];

export default function CoreOutcomes() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const headerEls = entry.target.querySelectorAll<HTMLElement>("[data-reveal]");
            headerEls.forEach((el, i) => {
              setTimeout(() => {
                el.style.opacity = "1";
                el.style.transform = "translateY(0)";
              }, i * 110);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="outcomes" className="bg-white" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-32">
        {/* Centered headline — Apple-scale */}
        <div className="text-center mb-24">
          <p
            data-reveal
            className="text-[11px] font-semibold uppercase tracking-[0.14em] text-bfx-accent mb-4"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Core Outcomes
          </p>
          <h2
            data-reveal
            className="text-4xl md:text-5xl lg:text-[60px] font-semibold text-bfx-primary leading-[1.05] tracking-tight"
            style={{ opacity: 0, transform: "translateY(20px)" }}
          >
            Engineered for Performance<br />
            <span className="text-bfx-secondary">and Control.</span>
          </h2>
        </div>

        {/* Three columns — borderless, visual-first */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {outcomes.map((o, i) => (
            <div
              key={o.title}
              data-reveal
              className="group flex flex-col p-10 rounded-2xl hover:bg-bfx-light transition-all duration-300 cursor-default"
              style={{ opacity: 0, transform: "translateY(20px)" }}
            >
              {/* Number */}
              <span className="text-[11px] font-semibold text-bfx-accent/60 tracking-widest mb-6 uppercase">
                {o.num}
              </span>

              {/* Large icon */}
              <div className="w-12 h-12 flex items-center justify-center mb-6 text-bfx-secondary">
                <i className={`${o.icon} text-3xl`} />
              </div>

              {/* Tag */}
              <span className="text-[11px] font-semibold text-bfx-accent tracking-widest uppercase mb-3">
                {o.tag}
              </span>

              {/* Title */}
              <h3 className="text-xl font-semibold text-bfx-primary leading-snug mb-4">
                {o.title}
              </h3>

              {/* Desc */}
              <p className="text-sm text-bfx-muted leading-relaxed flex-1">{o.desc}</p>

              {/* Bottom indicator */}
              <div className="mt-8 h-px w-10 bg-bfx-accent/40 group-hover:w-16 transition-all duration-300" />
            </div>
          ))}
        </div>

        {/* Visual spacer — abstract data image */}
        <div className="mt-24 rounded-2xl overflow-hidden relative h-64 md:h-80">
          <img
            src="https://public.readdy.ai/ai/img_res/dbdeef0349ba0d0c00583e4b9158a8fd.jpg"
            alt="Intelligence system visualization"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-bfx-primary/60 via-transparent to-bfx-graphite/60" />
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white text-xl md:text-2xl font-semibold tracking-tight text-center max-w-lg leading-snug">
              Intelligence that operates<br />
              <span className="text-bfx-accent">while you sleep.</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}